import * as React from 'react'
import {Drawer, Box, Typography, IconButton, Button} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {sectionCardSx, primaryBtnSx, secondaryBtnSx, alertStripSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, WARNING, INFO} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'
import {FRESH_WARN_BOLD, FRESH_WARN, FRESH_NOT_CARRIED, FRESH_BUDGET, FRESH_ON_FILE} from './copy'

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const bannerTextSx = {fontFamily: FONT, fontSize: SIZE.xs, lineHeight: 1.6, fontWeight: WEIGHT.regular}

/**
 * Weight Freeze (v2) — Requests: the fresh-request confirmation drawer.
 *
 * This drawer never builds a request itself — `onConfirm` is a plain
 * no-argument callback the parent (RequestsSection) uses to toast and
 * navigate to Initiate, which starts genuinely blank (map §4 gap: there is
 * no draft store and no selection handoff anywhere in this port).
 */
export default function RqFreshDrawer({req, open, onClose, onConfirm}) {
  if (!req) return null

  return (
    <Drawer anchor='right' open={open} onClose={onClose} sx={{'& .MuiDrawer-paper': {width: {xs: '100%', sm: 560}}}}>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <Box sx={{p: 2, borderBottom: `1px solid ${BORDER}`}}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1}}>
            <Box sx={{minWidth: 0}}>
              <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY}}>
                Raise a fresh request
              </Typography>
              <Typography sx={labelSx}>Replacing {req.id} · {req.subject || req.level}</Typography>
            </Box>
            <IconButton onClick={onClose} aria-label='Close fresh-request drawer' size='small'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{p: 2, overflowY: 'auto', flex: 1, display: 'grid', gap: GAP.md, alignContent: 'start'}}>
          <Box sx={{...alertStripSx(WARNING), alignItems: 'flex-start'}}>
            <Typography sx={bannerTextSx}>
              <Box component='span' sx={{fontWeight: WEIGHT.bold}}>{FRESH_WARN_BOLD}</Box>{FRESH_WARN}
            </Typography>
          </Box>

          <Box sx={sectionCardSx}>
            <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY, mb: 0.75}}>
              Nothing below is carried over from {req.id}
            </Typography>
            <Box component='ul' sx={{m: 0, pl: 2.5, display: 'grid', gap: 0.35}}>
              {FRESH_NOT_CARRIED.map((line) => (
                <Typography key={line} component='li' sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, lineHeight: 1.5}}>
                  {line}
                </Typography>
              ))}
            </Box>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, lineHeight: 1.6, mt: 1}}>
              {FRESH_BUDGET}
            </Typography>
          </Box>

          <Box sx={{...alertStripSx(INFO), alignItems: 'flex-start'}}>
            <Typography sx={bannerTextSx}>{FRESH_ON_FILE(req.id)}</Typography>
          </Box>
        </Box>

        <Box sx={{p: 1.5, borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 1, justifyContent: 'flex-end'}}>
          <Button sx={secondaryBtnSx} onClick={onClose}>Cancel</Button>
          <Button sx={primaryBtnSx} onClick={onConfirm}>Open a blank request</Button>
        </Box>
      </Box>
    </Drawer>
  )
}
